"use client";

import { Mail, Newspaper, Phone, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CoordinateCorners() {
  return <>
      <span aria-hidden className="pointer-events-none absolute left-0 top-0 h-2 w-2 border-l border-t border-[hsl(240_5%_90%)]" />
      <span aria-hidden className="pointer-events-none absolute right-0 top-0 h-2 w-2 border-r border-t border-[hsl(240_5%_90%)]" />
      <span aria-hidden className="pointer-events-none absolute left-0 bottom-0 h-2 w-2 border-l border-b border-[hsl(240_5%_90%)]" />
      <span aria-hidden className="pointer-events-none absolute right-0 bottom-0 h-2 w-2 border-r border-b border-[hsl(240_5%_90%)] transition-colors duration-300 group-hover:border-[hsl(65_100%_50%)]" />
    </>;
}
function ContactRow({
  label,
  value,
  href,
  Icon,
  terminusSide,
  index
}: {
  label: string;
  value: string;
  href: string;
  Icon: React.ComponentType<{
    className?: string;
  }>;
  terminusSide: "left" | "right";
  index: number;
}) {
  return <div data-index={index} className="relative py-8">
      <div className="flex items-start gap-6">
        <Icon className="mt-1.5 size-4 text-[hsl(65_100%_50%)]" />
        <div className="flex-1">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(240_5%_40%)]">
            {label}
          </div>
          <Link to={href} className="mt-2 block font-sans text-xl text-[hsl(240_10%_4%)] tracking-tight transition-colors hover:text-[hsl(65_100%_50%)]">
            {value}
          </Link>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 flex items-center">
        {terminusSide === "left" && <span aria-hidden className="size-1.5 bg-[hsl(65_100%_50%)]" />}
        <span aria-hidden className="h-px flex-1 bg-[hsl(240_5%_90%)]" />
        {terminusSide === "right" && <span aria-hidden className="size-1.5 bg-[hsl(65_100%_50%)]" />}
      </div>
    </div>;
}
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-[hsl(240_5%_90%)] bg-[hsl(0_0%_100%)]">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(65_100%_50%)]">
          § Request Processed
        </div>
        <p className="mt-4 font-sans text-2xl text-[hsl(240_10%_4%)] tracking-tight">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" aria-label="Send another inquiry" className="mt-6 border-[hsl(240_10%_4%)] bg-transparent text-[hsl(240_10%_4%)] font-mono text-[13px] uppercase tracking-[0.04em] hover:bg-[hsl(240_10%_4%)] hover:text-[hsl(0_0%_98%)]">
          Send another inquiry
        </Button>
      </div>;
  }
  const inputClasses = "h-11 rounded-none border-0 border-b border-[hsl(240_5%_90%)] bg-transparent px-0 text-[hsl(240_10%_4%)] placeholder:text-[hsl(240_5%_40%)]/50 shadow-none focus-visible:border-[hsl(65_100%_50%)] focus-visible:ring-0 focus-visible:ring-offset-0";
  const labelClasses = "font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(240_5%_40%)]";
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="name" className={labelClasses}>Client Name</FormLabel>
              <FormControl>
                <Input id="name" placeholder="Your distinguished name" className={inputClasses} aria-describedby="name-error" {...field} />
              </FormControl>
              <FormMessage id="name-error" role="alert" />
            </FormItem>} />
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="email" className={labelClasses}>Private Email</FormLabel>
              <FormControl>
                <Input id="email" type="email" placeholder="email@atelier.com" className={inputClasses} aria-describedby="email-error" {...field} />
              </FormControl>
              <FormMessage id="email-error" role="alert" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="phone" className={labelClasses}>Direct Contact</FormLabel>
              <FormControl>
                <Input id="phone" type="tel" placeholder="+1 (000) 000-0000" className={inputClasses} aria-describedby="phone-error" {...field} />
              </FormControl>
              <FormMessage id="phone-error" role="alert" />
            </FormItem>} />
        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel htmlFor="subject" className={labelClasses}>Service Selection</FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value || ""}>
                  <SelectTrigger id="subject" aria-describedby="subject-error" className="h-11 border-0 border-b border-[hsl(240_5%_90%)] bg-transparent px-0 text-[hsl(240_10%_4%)] focus-visible:border-[hsl(65_100%_50%)] focus-visible:ring-0 data-[placeholder]:text-[hsl(240_5%_40%)]/50">
                    <SelectValue placeholder="Select your requirement" />
                  </SelectTrigger>
                  <SelectContent className="bg-[hsl(0_0%_100%)] border-[hsl(240_5%_90%)] text-[hsl(240_10%_4%)]">
                    <SelectItem value="styling">Personal Styling Consultation</SelectItem>
                    <SelectItem value="vip">VIP Access Request</SelectItem>
                    <SelectItem value="custom">Custom Order Inquiry</SelectItem>
                    <SelectItem value="orders">Order Status</SelectItem>
                    <SelectItem value="returns">Returns & Exchanges</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage id="subject-error" role="alert" />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel htmlFor="message" className={labelClasses}>Inquiry Details</FormLabel>
            <FormControl>
              <Textarea id="message" rows={6} placeholder="Please describe your request for our concierge team..." className="min-h-[160px] border-0 border-b border-[hsl(240_5%_90%)] bg-transparent px-0 text-[hsl(240_10%_4%)] placeholder:text-[hsl(240_5%_40%)]/50 focus-visible:border-[hsl(65_100%_50%)] focus-visible:ring-0 focus-visible:ring-offset-0 resize-none" aria-describedby="message-error" {...field} />
            </FormControl>
            <FormMessage id="message-error" role="alert" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-[hsl(0_84%_60%)]/50 bg-[hsl(0_84%_60%)]/10">
          <p className="text-[hsl(0_84%_60%)] text-sm">{submitError}</p>
        </div>}

      <div className="pt-6">
        <div className="flex items-center mb-6">
          <span aria-hidden className="size-1.5 bg-[hsl(65_100%_50%)]" />
          <span aria-hidden className="h-px flex-1 bg-[hsl(240_5%_90%)]" />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(240_5%_40%)]">
            Response within 24 hours
          </p>
          <Button type="submit" disabled={isSubmitting} aria-label="Submit Inquiry" className="bg-[hsl(240_10%_4%)] px-8 h-12 font-mono text-[13px] uppercase tracking-[0.04em] font-medium text-[hsl(0_0%_98%)] hover:bg-[hsl(240_10%_4%)]/90 transition-all duration-200">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Sending
              </> : "Submit Inquiry"}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function Assistance() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const contactRows = [{
    label: "Atelier",
    value: "(763) 355-8271",
    href: "tel:+17633558271",
    Icon: Phone,
    terminusSide: "right" as const
  }, {
    label: "Email",
    value: "info@driplocations.com",
    href: "mailto:info@driplocations.com",
    Icon: Mail,
    terminusSide: "left" as const
  }, {
    label: "Press",
    value: "info@driplocations.com",
    href: "mailto:info@driplocations.com",
    Icon: Newspaper,
    terminusSide: "right" as const
  }];
  return <section data-section-id="2152" id="style-assistance" className="relative bg-[hsl(0_0%_98%)] text-[hsl(240_10%_4%)] py-24 md:py-40 overflow-hidden">
      <div ref={ref} className={`mx-auto max-w-[1440px] px-6 md:px-12 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28">
              <div className="flex items-center gap-4 mb-10">
                <span aria-hidden className="h-px w-6 bg-[hsl(240_10%_4%)]" />
                <span className="font-mono text-[12px] uppercase tracking-[0.18em] text-[hsl(240_5%_40%)]">
                  § N° 01 — Concierge
                </span>
              </div>

              <h1 className="font-sans text-[hsl(240_10%_4%)] font-[900]" style={{
              fontSize: "clamp(3rem, 8vw, 7.5rem)",
              lineHeight: 0.9,
              letterSpacing: "-0.04em"
            }}>
                Style{" "}
                <span className="relative inline-block">
                  Assistance
                  <span aria-hidden className="absolute left-0 -bottom-2 flex items-center w-full">
                    <span className="h-px flex-1 bg-[hsl(240_10%_4%)]" />
                    <span className="size-1.5 bg-[hsl(65_100%_50%)]" />
                  </span>
                </span>
              </h1>

              <p className="mt-12 text-[hsl(240_5%_40%)] max-w-[480px]" style={{
              fontSize: "clamp(1.125rem, 1.5vw, 1.25rem)",
              lineHeight: 1.6
            }}>
                Welcome to Drip District support. Whether you need assistance with sizing, order tracking, or styling advice, our support team is ready to provide you with prompt and reliable service.
              </p>

              <div className="mt-12">
                <div className="h-px w-full bg-[hsl(240_5%_90%)] mb-2" />
                {contactRows.map((row, i) => <ContactRow key={row.label} index={i} label={row.label} value={row.value} href={row.href} Icon={row.Icon} terminusSide={row.terminusSide} />)}
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="group relative bg-[hsl(0_0%_100%)] border border-[hsl(240_5%_90%)] p-8 md:p-12 lg:p-14">
              <CoordinateCorners />

              <div className="flex items-center gap-4 mb-10">
                <span aria-hidden className="size-1.5 bg-[hsl(65_100%_50%)]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(240_5%_40%)]">
                  Inquiry / Drip District
                </span>
              </div>

              <FormProvider formKey="style_assistance_form_customer_care" formId="style_assistance_form_customer_care" sectionName="style-assistance" defaultValues={{
              name: "",
              email: "",
              phone: "",
              subject: "",
              message: ""
            }} validationRules={{
              name: {
                required: true,
                minLength: 2,
                maxLength: 80
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: false,
                pattern: "phone"
              },
              subject: {
                required: false
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Submit Inquiry" successMessage="Your message has been successfully received by Drip District support. We will get back to you shortly." errorMessage="We encountered an issue submitting your message. Please try again or reach out to us directly.">
                <ContactFormFields />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}